import React, { useMemo } from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { BusinessAppWrapper } from '@atlassian/jira-business-app-wrapper';
import { useEscToCloseModalFromIframe } from '@atlassian/jira-business-confluence-pages';
import { PerformanceAnalytics } from '@atlassian/jira-business-performance';
import { CORE_PROJECT } from '@atlassian/jira-common-constants';
import { Tokens } from '@atlassian/jira-custom-theme-constants';
import { ReportErrors } from '@atlassian/jira-errors-handling';
import { ContextualAnalyticsData, SCREEN } from '@atlassian/jira-product-analytics-bridge';
import ProjectPagesApp from '@atlassian/jira-project-pages';
import type { ProjectPagesTheme } from '@atlassian/jira-project-pages/src/view/types';
import { useResource } from '@atlassian/jira-router';
import { projectContextResource } from '@atlassian/jira-router-resources-project-context';
import AddProjectToHistory from '@atlassian/jira-spa-apps-common/src/add-project-to-history';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';

const PagesPerformanceTracking = ({ loading }: { loading: boolean }) => (
	<PerformanceAnalytics view="jwm-pages" loading={loading} />
);

export const JiraProjectPagesApp = () => {
	const projectContext = useResource(projectContextResource);
	const tenantContext = useTenantContext();
	const { isAdmin, baseUrl, locale, cloudId, atlassianAccountId, environment } = tenantContext;
	const analyticsSourceName = 'jwmpages';
	const analyticsContext = {
		projectType: CORE_PROJECT,
	};

	const themeWithOverrideStyles: ProjectPagesTheme = useMemo(
		() => ({
			name: 'business',
			type: 'business',
			background: { color: 'inherit' },
			color: { primary: Tokens.COLOR_TEXT },
			navigation: {
				icon: {
					textColor: Tokens.COLOR_TEXT,
					color: Tokens.COLOR_TEXT,
					gradientStart: Tokens.COLOR_TEXT,
					gradientStop: Tokens.COLOR_TEXT_SUBTLE,
				},
			},
			overrideStyles: {
				mainContainer: {
					backgroundColor: token('elevation.surface', 'white'),
					color: token('color.text', 'black'),
					borderRadius: '8px',
					padding: token('space.150', '12px'),
				},
				pageHeader: {
					wrapper: {
						backgroundColor: Tokens.ELEVATION_SURFACE_CONTRAST,
						padding: `0 ${token('space.200', '16px')}`,
						margin: `${token('space.300', '24px')} 0 ${token('space.200', '16px')} 0`,
						borderRadius: token('border.radius.200', '8px'),
					},
				},
				pageTitleStyleWrapper: {
					color: Tokens.COLOR_TEXT,
					// The API expects a number here

					marginTop: 6,
					outline: 'none',
					fontsize: '24px',
					lineHeight: token('space.200', '4px'),
				},
				tableTreeWrapper: {
					borderRadius: token('border.radius.200', '8px'),
				},
				poweredBy: {
					color: Tokens.COLOR_TEXT,
				},
				contentWrapper: {
					padding: '0',
				},
				sidebarWrapper: {
					backgroundColor: token('elevation.surface', 'white'),
					border: 'none',
					borderRadius: `${token('border.radius.100', '6px')} ${token('border.radius.200', '8px')} 0 0`,
					boxShadow: token(
						'elevation.shadow.overlay',
						`0px 8px 12px ${colors.N40A}, 0px 0px 1px ${colors.N60A}`,
					),
					height: 'auto',
					margin: `${token('space.200', '16px')} 0 0`,
					padding: `${token('space.100', '8px')} ${token('space.250', '20px')}`,
				},
				viewContainer: {
					padding: '0',
				},
			},
		}),
		[],
	);

	useEscToCloseModalFromIframe();

	if (!(projectContext && projectContext.data && projectContext.data.projectKey)) {
		return null;
	}

	const { projectId, projectKey, projectName, projectType } = projectContext.data;

	return (
		<ContextualAnalyticsData
			containerId={projectId.toString()}
			containerType="project"
			objectType="pages"
			attributes={analyticsContext}
			sourceType={SCREEN}
			sourceName={analyticsSourceName}
		>
			<BusinessAppWrapper shouldAddPadding={false}>
				<ReportErrors id="unhandled" packageName="jiraSpaAppsBusinessPages" teamName="jira-warepil">
					<Box xcss={pageLayoutStyles}>
						<ProjectPagesApp
							baseUrl={baseUrl}
							locale={locale}
							isAdmin={isAdmin}
							cloudId={cloudId}
							accountId={atlassianAccountId}
							projectId={projectId}
							projectKey={projectKey}
							projectName={projectName}
							projectType={projectType}
							environment={environment}
							theme={themeWithOverrideStyles}
							breadcrumbs={null}
							PerformanceTracking={PagesPerformanceTracking}
							parentProduct="JWM"
						/>
					</Box>
				</ReportErrors>
			</BusinessAppWrapper>
			<AddProjectToHistory baseUrl={baseUrl} projectKey={projectKey} />
		</ContextualAnalyticsData>
	);
};

const pageLayoutStyles = xcss({
	height: '100%',
	marginBlock: '0',
	marginInline: 'space.500',
});
